import type { TeacherSessionSummary } from "@studio/shared";
import { formatRelativeTime } from "../../lib/relativeTime";
import { retentionView } from "../../lib/retention";

/**
 * 교사 대시보드 세션 카드 — BUILDER-UX-POLICY §4 「세션 카드 (D안: 미니 대시보드)」.
 *
 * 🔑 카드는 **단순 바로가기 버튼이 아니라 수업 현황 미니 대시보드**다.
 *    교사가 카드를 열지 않고도 참여·완성·진행중을 파악할 수 있어야 한다(정책 본문).
 *    이전 studio 구현은 3열 그리드의 「제목 + 한 줄 요약 + 버튼」이라 목록이었지 현황판이 아니었다.
 *
 * 하단 활동 피드(아바타 4개 + 최근 1건 + 상대 시간)는 서버 `listSessions` 가 실어 준다.
 *
 * 🚨 «+N» 은 반드시 `activity_count - 4` 다. `recent_students` 는 **서버에서 top 4 로 잘리므로**
 *    `recent_students.length > 4` 로 비교하면 영원히 거짓인 죽은 코드가 된다(정책이 명시한 함정).
 */
export function SessionCard({
  session,
  onOpen,
  onQr,
  onDelete,
  onExtendRetention,
  extending = false,
}: {
  session: TeacherSessionSummary;
  onOpen: () => void;
  onQr: () => void;
  onDelete: () => void;
  onExtendRetention?: () => void;
  extending?: boolean;
}) {
  const live = session.status === "live";
  const madeLabel = session.kind === "mv"
    ? "완성 영상"
    : session.kind === "image" ? "완성 작품" : session.kind === "poster" ? "인쇄 확정" : "완성 곡";
  // 🔑 영상·그림은 서버가 센 완성 수(`completed_count`), 음악은 만든 곡 수다.
  const madeCount = session.kind === "music" ? session.track_count : session.completed_count;
  // 🚨 «+N» 은 recent_students 길이가 아니라 activity_count 로 센다(위 주석의 그 함정).
  const overflow = Math.max(0, session.activity_count - session.recent_students.length);
  // 보관 규칙(2026-10-01): 끝난 수업의 AI 그림·영상은 학년도 끝(2월 말)에 지워진다 — 2주 전부터 예고.
  const retention = live ? { kind: "none" as const } : retentionView(session.retention);

  return (
    <article
      className={`teacher-session-card${live ? "" : " is-ended"}`}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        onOpen();
      }}
    >
      <div className="teacher-session-main">
        <div className="teacher-session-code-tile">{session.code}</div>

        <div className="teacher-session-copy">
          <div className="teacher-session-title-row">
            <h3>{session.title}</h3>
            <span className={`teacher-status ${session.status}`}>{live ? "진행 중" : "종료"}</span>
            {session.is_demo ? <span className="teacher-demo-badge">시연작</span> : null}
          </div>
          <p className="teacher-session-time">{formatRelativeTime(session.created_at)}</p>
        </div>

        <div className="teacher-session-actions">
          {live ? (
            <button
              type="button"
              className="teacher-session-qr"
              aria-label={`${session.title} 참여 QR 보기`}
              onClick={(event) => {
                event.stopPropagation();
                onQr();
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="3" width="6" height="6" rx="1" />
                <rect x="15" y="3" width="6" height="6" rx="1" />
                <rect x="3" y="15" width="6" height="6" rx="1" />
                <path d="M15 12 L21 12 M15 15 L18 15 L18 21 L15 21 L15 18" />
              </svg>
              QR코드
            </button>
          ) : null}
          <button
            type="button"
            className={live ? "button-primary" : "button-secondary"}
            onClick={(event) => {
              event.stopPropagation();
              onOpen();
            }}
          >
            {live ? "라이브 보기" : "결과 보기"}
          </button>
          {/*
            🚨 삭제는 **종료된 수업에만** 둔다(BUILDER-UX §4 D5). 진행 중 카드에서
               「라이브 보기」 옆에 삭제가 있으면, 한 칸 차이로 «수업 중인 반의 곡·영상»이
               통째로 사라진다. 지우려면 먼저 수업을 종료하면 된다.
            🚨 카드 전체가 «열기» 버튼이라 여기서 stopPropagation 을 빠뜨리면
               삭제를 누른 순간 상세로도 함께 이동한다(모달이 뜬 채 화면이 바뀐다).
               삭제 자체는 여기서 하지 않는다 — 확인 모달을 띄우는 것까지가 카드의 일.
          */}
          {live ? null : (
            <button
              type="button"
              className="teacher-session-delete"
              aria-label={`${session.title} 수업 삭제`}
              onClick={(event) => {
                event.stopPropagation();
                onDelete();
              }}
            >
              삭제
            </button>
          )}
        </div>
      </div>

      <div className="teacher-session-stats">
        <div>
          <b>{session.participant_count}</b>
          <span>참여 학생</span>
        </div>
        <div>
          <b>{madeCount}</b>
          <span>{madeLabel}</span>
        </div>
        <div>
          <b>{session.in_progress_count}</b>
          <span>진행 중</span>
        </div>
      </div>

      {retention.kind === "kept" ? (
        <p className="teacher-session-retention">
          AI 그림·영상 {retention.count}개 · {retention.dateLabel}까지 보관
        </p>
      ) : null}
      {retention.kind === "soon" ? (
        <div className="teacher-session-retention is-soon" role="status">
          <p>
            {retention.daysLeft === 0
              ? `AI 그림·영상 ${retention.count}개가 곧 지워져요`
              : `AI 그림·영상 ${retention.count}개가 ${retention.daysLeft}일 뒤(${retention.dateLabel}) 지워져요`}
          </p>
          {onExtendRetention && retention.daysLeft > 0 ? (
            <button
              type="button"
              className="button-secondary"
              disabled={extending}
              onClick={(event) => {
                // 카드 전체가 «열기» 버튼이다 — 막지 않으면 상세로도 함께 이동한다.
                event.stopPropagation();
                onExtendRetention();
              }}
            >
              {extending ? "미루는 중..." : "1년 더 보관"}
            </button>
          ) : null}
        </div>
      ) : null}

      {session.last_activity
        ? (
          <div className="teacher-session-feed">
            <div className="teacher-session-avatars" aria-hidden="true">
              {session.recent_students.map((name, index) => (
                <span key={`${name}-${index}`} className={`teacher-avatar teacher-avatar-${index % 4}`}>
                  {[...name][0] ?? "?"}
                </span>
              ))}
              {overflow > 0 ? <span className="teacher-avatar teacher-avatar-more">+{overflow}</span> : null}
            </div>
            <p className="teacher-session-feed-text">
              <b>{session.last_activity.student_name}</b>
              {` · ${session.last_activity.target_title} ${session.last_activity.action}`}
            </p>
            <span className="teacher-session-feed-time">
              {formatRelativeTime(session.last_activity.timestamp, { mode: "compact" })}
            </span>
          </div>
        )
        : (
          <p className="teacher-session-foot">
            {live
              ? "QR 코드를 학생에게 공유하면 여기에 참여 현황이 표시돼요"
              : "활동 없이 끝난 수업이에요"}
          </p>
        )}
    </article>
  );
}
